/**
 * Reviews → Settings → Procedure code exclusions: option list for the vue-multiselect picker.
 *
 * ⛔ IMPORT-FREE ON PURPOSE. cypress/node-tests/reviews-settings-timing-exclusions.test.js
 * strips the `export`s and executes THIS file. Only `export function` here.
 *
 * Modelled on Insurance Verification's buildProcCodeOptions / procOptionsForSelection
 * (InsuranceVerification/components/constants.js), with two deliberate differences:
 *   - it reads the Reviews store shape { codeNum, procCode, descript } (camelCase, from
 *     /get-review-proc-codes), not IV's raw { CodeNum, ProcCode, Descript } rows;
 *   - it does NOT filter to CDT shape or change case. The worker compares the stored strings
 *     to procedurecode.ProcCode exactly, and an office's custom in-house codes are perfectly
 *     good things to exclude.
 */

/**
 * buildExclusionOptions(rows, selectedCodes) → [{ value, text, desc }]
 *
 * ⛔ `value` is ALWAYS the procCode, never the codeNum. excludedProcCodes is matched against
 * ProcCode strings by the worker; a codeNum ("160") saved here renders a chip, saves fine,
 * and excludes nothing — with no error anywhere.
 *
 * De-duplicated (first row wins), sorted by code. A selected code that is missing from the
 * catalog is still included (text = the bare code), so its chip shows as selected and can
 * be removed.
 */
export function buildExclusionOptions(rows, selectedCodes) {
  const byCode = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const code = row.procCode;
    if (typeof code !== 'string' || code.trim() === '' || byCode.has(code)) return;
    const desc = typeof row.descript === 'string' ? row.descript.trim() : '';
    byCode.set(code, { value: code, text: desc ? `${code} - ${desc}` : code, desc });
  });

  (Array.isArray(selectedCodes) ? selectedCodes : []).forEach((code) => {
    if (typeof code !== 'string' || code.trim() === '' || byCode.has(code)) return;
    byCode.set(code, { value: code, text: code, desc: '' });
  });

  return Array.from(byCode.values()).sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
}

/**
 * exclusionOptionsForSelection(options, selectedCodes) → [{ value, text, desc }]
 *
 * The picker's :value, in the stored order. ⛔ Never FILTERS against `options`: a code the
 * office no longer carries (or a catalog that failed to load) falls back to a bare option, so
 * the exclusion stays visible and removable instead of silently vanishing on the next Save.
 */
export function exclusionOptionsForSelection(options, selectedCodes) {
  const map = {};
  (Array.isArray(options) ? options : []).forEach((o) => {
    if (o && typeof o.value === 'string') map[o.value] = o;
  });
  return (Array.isArray(selectedCodes) ? selectedCodes : [])
    .filter((code) => typeof code === 'string' && code.trim() !== '')
    .map((code) => map[code] || { value: code, text: code, desc: '' });
}
